import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import type { User, EnableMfaResponse } from '@sss/sdk';
import { sdk } from '../../lib/sdk';

interface Props {
  me: User;
  onUpdated: (user: User) => void;
}

export default function SecurityTab({ me, onUpdated }: Props) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <PasswordSection me={me} onUpdated={onUpdated} />
      <MfaSection me={me} onUpdated={onUpdated} />
    </div>
  );
}

function PasswordSection({ me, onUpdated }: Props) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const isOauthOnly = !me.hasPassword;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (next !== confirm) {
      setMsg({ kind: 'err', text: t('settings.security.mismatched') });
      return;
    }
    setBusy(true);
    try {
      if (isOauthOnly) {
        await sdk.auth.setPassword(next, me.mfaEnabled ? mfaToken : undefined);
      } else {
        await sdk.auth.changePassword(current, next, me.mfaEnabled ? mfaToken : undefined);
      }
      // hasPassword flips to true after setPassword — refresh user so the
      // section flips from "set" to "change" without a page reload.
      const updated = await sdk.users.me();
      onUpdated(updated);
      setCurrent('');
      setNext('');
      setConfirm('');
      setMfaToken('');
      setMsg({ kind: 'ok', text: t('settings.security.passwordUpdated') });
    } catch (err) {
      setMsg({ kind: 'err', text: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h3>{isOauthOnly ? t('settings.security.setPassword') : t('settings.security.changePassword')}</h3>
      {isOauthOnly && <p style={{ color: '#666' }}>{t('settings.security.setPasswordHint')}</p>}
      <form onSubmit={onSubmit} style={col}>
        {!isOauthOnly && (
          <label style={label}>
            {t('settings.security.currentPassword')}
            <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required style={input} />
          </label>
        )}
        <label style={label}>
          {t('settings.security.newPassword')}
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} required style={input} />
        </label>
        <label style={label}>
          {t('settings.security.confirmPassword')}
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required style={input} />
        </label>
        {me.mfaEnabled && (
          <label style={label}>
            {t('settings.security.mfa.mfaToken')}
            <input value={mfaToken} onChange={(e) => setMfaToken(e.target.value)} required style={input} />
          </label>
        )}
        <div>
          <button type="submit" disabled={busy} style={btn}>
            {busy ? t('common.loading') : t('common.save')}
          </button>
        </div>
        {msg && <p style={{ color: msg.kind === 'ok' ? 'green' : 'red' }}>{msg.text}</p>}
      </form>
    </section>
  );
}

function MfaSection({ me, onUpdated }: Props) {
  const { t } = useTranslation();
  const [enroll, setEnroll] = useState<EnableMfaResponse | null>(null);
  const [password, setPassword] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [disableToken, setDisableToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function startEnable(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // Re-authenticate before enrolment (skipped for OAuth-only accounts).
      const result = await sdk.auth.enableMfa(me.hasPassword ? password : undefined);
      setPassword('');
      setEnroll(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnable(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // Confirming enrolment revokes other sessions; the SDK stores the
      // fresh pair it returns via onTokensRefreshed.
      await sdk.auth.confirmMfa(confirmCode);
      const updated = await sdk.users.me();
      onUpdated(updated);
      setEnroll(null);
      setConfirmCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function onDisable(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await sdk.auth.disableMfa(disableToken);
      const updated = await sdk.users.me();
      onUpdated(updated);
      setDisableToken('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h3>{t('settings.security.mfa.title')}</h3>
      <p style={{ color: '#666' }}>
        {me.mfaEnabled ? t('settings.security.mfa.enabled') : t('settings.security.mfa.disabled')}
      </p>

      {!me.mfaEnabled && !enroll && (
        <form onSubmit={startEnable} style={col}>
          {me.hasPassword && (
            <label style={label}>
              {t('settings.security.currentPassword')}
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required style={input} />
            </label>
          )}
          <div>
            <button type="submit" disabled={busy} style={btn}>
              {t('settings.security.mfa.enable')}
            </button>
          </div>
        </form>
      )}

      {enroll && (
        <div style={col}>
          <p>{t('settings.security.mfa.scanQr')}</p>
          <div style={qrBox}>
            <QRCodeSVG value={enroll.otpauth} size={192} level="M" />
          </div>
          <p>
            <strong>{t('settings.security.mfa.secret')}:</strong>{' '}
            <code style={{ wordBreak: 'break-all' }}>{enroll.mfaSecret}</code>
          </p>
          <details>
            <summary>{t('settings.security.mfa.backupCodes')}</summary>
            <p style={{ color: '#666' }}>{t('settings.security.mfa.backupCodesHint')}</p>
            <ul>
              {enroll.backupCodes.map((c) => (
                <li key={c}><code>{c}</code></li>
              ))}
            </ul>
          </details>
          <form onSubmit={confirmEnable} style={col}>
            <label style={label}>
              {t('settings.security.mfa.verifyToken')}
              <input value={confirmCode} onChange={(e) => setConfirmCode(e.target.value)} required style={input} />
            </label>
            <div>
              <button type="submit" disabled={busy} style={btn}>
                {t('settings.security.mfa.verify')}
              </button>
            </div>
          </form>
        </div>
      )}

      {me.mfaEnabled && (
        <form onSubmit={onDisable} style={col}>
          <label style={label}>
            {t('settings.security.mfa.mfaToken')}
            <input value={disableToken} onChange={(e) => setDisableToken(e.target.value)} required style={input} />
          </label>
          <div>
            <button type="submit" disabled={busy} style={btn}>
              {t('settings.security.mfa.disable')}
            </button>
          </div>
        </form>
      )}

      {error && <p style={{ color: 'red' }}>{error}</p>}
    </section>
  );
}

const col: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '1rem' };
const label: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.25rem' };
const input: React.CSSProperties = { padding: '0.5rem', border: '1px solid #ccc', borderRadius: 4, font: 'inherit' };
const btn: React.CSSProperties = { padding: '0.5rem 1rem', cursor: 'pointer' };
const qrBox: React.CSSProperties = {
  display: 'inline-block',
  padding: '12px',
  background: '#fff',
  borderRadius: 4,
  border: '1px solid #eee',
  alignSelf: 'flex-start',
};
